import { useState, useCallback, useMemo, useRef } from 'react';
import { Head } from '@inertiajs/react';
import { router } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { AddressForm } from '../../components/address/AddressForm';
import { dataLayerPushAsync } from '../../utils/tracking';
import type { CompanySetupProps, CompanySetupTier } from '../../types/company-setup';

interface CuitData {
    cuit: string;
    name: string;
    iva_condition: number;
    iva_condition_name: string;
}

const RUBRO_ICONS: Record<string, string> = {
    'Bar': 'fa-wine-glass',
    'Restaurante': 'fa-utensils',
    'Hotel': 'fa-building',
    'Catering': 'fa-users',
    'Supermercado': 'fa-cart-shopping',
    'Almacen': 'fa-basket-shopping',
    'Verdulería': 'fa-carrot',
    'Otro': 'fa-ellipsis',
};

const STEP_SUBTITLES: Record<number, string> = {
    1: 'Ingresá el CUIT de tu negocio para comenzar.',
    2: 'Esto nos permite asignarte la mejor lista de precios.',
    3: 'Agregá la dirección de entrega de tu negocio.',
};

function formatCurrency(value: number): string {
    if (value >= 1_000_000) {
        const m = value / 1_000_000;
        return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
}

function getTierForVolume(tiers: CompanySetupTier[], volume: number): CompanySetupTier {
    for (let i = tiers.length - 1; i >= 0; i--) {
        if (volume >= tiers[i].min_volume) return tiers[i];
    }
    return tiers[0];
}

function getInitialRubro(rubros: Record<string, string>): string {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    const rubro = params.get('rubro');
    if (rubro && rubros[rubro]) return rubro;
    return '';
}

export default function Create({ ivaConditions, rubros, addressRoutes, tiers, promoDiscount, leadToken, existingAddresses }: CompanySetupProps) {
    const totalSteps = 3;

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [cuit, setCuit] = useState('');
    const [cuitData, setCuitData] = useState<CuitData | null>(null);
    const [cuitLoading, setCuitLoading] = useState(false);
    const [cuitError, setCuitError] = useState('');
    const [type, setType] = useState(() => getInitialRubro(rubros));
    const [volume, setVolume] = useState(500000);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
    const [showNewForm, setShowNewForm] = useState(false);
    const hasExistingAddresses = existingAddresses.length > 0;

    const activeTier = useMemo(() => getTierForVolume(tiers, volume), [tiers, volume]);

    const lookupCuit = useCallback((value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        setCuitData(null);
        setCuitError('');

        if (value.length !== 11) return;

        debounceRef.current = setTimeout(async () => {
            setCuitLoading(true);
            try {
                const res = await fetch(`/api/cuit/${value}`);
                if (!res.ok) {
                    setCuitError('CUIT no encontrado en AFIP.');
                    return;
                }
                const data: CuitData = await res.json();
                if (data.iva_condition === 5) {
                    setCuitError('El CUIT ingresado corresponde a un Consumidor Final. Solo se pueden registrar empresas.');
                    return;
                }
                setCuitData(data);
            } catch {
                setCuitError('Error al consultar AFIP. Intentá de nuevo.');
            } finally {
                setCuitLoading(false);
            }
        }, 500);
    }, []);

    const handleCuitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 11);
        setCuit(value);
        setErrors(prev => ({ ...prev, cuit: '' }));
        lookupCuit(value);
    };

    const handleStep1Continue = (e: React.FormEvent) => {
        e.preventDefault();
        if (!cuitData) {
            setErrors({ cuit: 'Consultá un CUIT válido primero.' });
            return;
        }
        if (!type) {
            setErrors({ type: 'Seleccioná un rubro.' });
            return;
        }
        dataLayerPushAsync({
            event: 'company_setup_step_completed',
            step: 1,
            rubro: type,
        });
        setStep(2);
    };

    const submitCompany = (locationId?: number) => {
        if (!cuitData) return;

        setSubmitting(true);
        router.post('/mi-empresa', {
            cuit: cuitData.cuit,
            name: cuitData.name,
            iva_condition: cuitData.iva_condition,
            type,
            monthly_commitment: volume,
            lead_token: leadToken || undefined,
            location_id: locationId || undefined,
        }, {
            onError: (errs) => {
                setErrors(errs);
                setSubmitting(false);
                setStep(1);
            },
        });
    };

    const handleStep2Continue = (e: React.FormEvent) => {
        e.preventDefault();
        dataLayerPushAsync({
            event: 'company_setup_step_completed',
            step: 2,
            volume,
            tier: activeTier.name,
        });
        setStep(3);
    };

    const handleAddressSuccess = (newId?: number) => {
        submitCompany(newId);
    };

    return (
        <MainLayout>
            <Head>
                <title>Crear empresa | Abasto Virtual</title>
                <meta name="robots" content="noindex" />
            </Head>

            <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
                <div className={`w-full rounded-2xl bg-white p-8 shadow-lg ${step === 3 ? 'max-w-lg' : 'max-w-md'}`}>
                    <div className="mb-6 text-center">
                        <img
                            src="https://cdn.abastovirtual.com/img/logo.webp"
                            alt="Logo de AbastoVirtual"
                            className="mx-auto mb-4 w-40"
                        />
                        <h1 className="text-2xl font-bold text-gray-900">Creá tu empresa</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            {STEP_SUBTITLES[step]}
                        </p>
                        {/* Step indicator */}
                        <div className="mt-4 flex items-center justify-center gap-2">
                            {Array.from({ length: totalSteps }, (_, i) => (
                                <div
                                    key={i}
                                    className={`h-1.5 w-12 rounded-full ${i + 1 <= step ? 'bg-green-500' : 'bg-green-200'}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Step 1: CUIT + Rubro */}
                    {step === 1 && (
                        <form onSubmit={handleStep1Continue} className="space-y-5">
                            {/* CUIT */}
                            <div>
                                <label htmlFor="cuit" className="mb-1 block text-sm font-medium text-gray-700">
                                    CUIT
                                </label>
                                <div className="relative">
                                    <input
                                        id="cuit"
                                        type="text"
                                        inputMode="numeric"
                                        value={cuit}
                                        onChange={handleCuitChange}
                                        placeholder="Ej: 30712345678"
                                        className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-green-500 focus:ring-1 focus:ring-green-500 ${
                                            cuitError || errors.cuit ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        maxLength={11}
                                    />
                                    {cuitLoading && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-500 border-t-transparent" />
                                        </div>
                                    )}
                                </div>
                                {(cuitError || errors.cuit) && (
                                    <p className="mt-1 text-xs text-red-600">{cuitError || errors.cuit}</p>
                                )}
                            </div>

                            {/* Razón Social (auto-populated) */}
                            {cuitData && (
                                <>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">
                                            Razón Social
                                        </label>
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
                                            {cuitData.name}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">
                                            Condición de IVA
                                        </label>
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
                                            {cuitData.iva_condition_name || ivaConditions[cuitData.iva_condition] || 'No determinada'}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Rubro */}
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Rubro
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(rubros)
                                        .filter(([, label]) => label !== 'Consumidor Final')
                                        .map(([key, label]) => {
                                            const icon = RUBRO_ICONS[label] || 'fa-store';
                                            const selected = type === key;
                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => {
                                                        setType(key);
                                                        setErrors(prev => ({ ...prev, type: '' }));
                                                    }}
                                                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                                                        selected
                                                            ? 'border-green-500 bg-green-50 text-green-700 ring-1 ring-green-500'
                                                            : errors.type
                                                                ? 'border-red-300 text-gray-700 hover:border-gray-400'
                                                                : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                                    }`}
                                                >
                                                    <i className={`fa ${icon} ${selected ? 'text-green-600' : 'text-gray-400'}`} style={{ width: 18, textAlign: 'center' }} />
                                                    {label}
                                                </button>
                                            );
                                        })}
                                </div>
                                {errors.type && (
                                    <p className="mt-1 text-xs text-red-600">{errors.type}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={!cuitData || !type}
                                className="w-full rounded-lg bg-green-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Continuar
                            </button>
                        </form>
                    )}

                    {/* Step 2: Volumen estimado con slider */}
                    {step === 2 && (
                        <form onSubmit={handleStep2Continue} className="space-y-5">
                            <p className="text-sm text-gray-500">
                                Indicá un aproximado de cuánto pensás comprar por mes.
                            </p>

                            {/* Volume display */}
                            <div className="rounded-xl border border-gray-200 bg-gray-50 py-4 text-center">
                                <p className="text-xs text-gray-500">Volumen estimado mensual</p>
                                <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(volume)}/mes</p>
                            </div>

                            {/* Slider */}
                            <div>
                                <input
                                    type="range"
                                    min={200000}
                                    max={5000000}
                                    step={50000}
                                    value={volume}
                                    onChange={e => setVolume(Number(e.target.value))}
                                    className="w-full accent-green-500"
                                />
                                <div className="flex justify-between text-xs text-gray-400">
                                    <span>$200K</span>
                                    <span>$5M</span>
                                </div>
                            </div>

                            {/* Tier cards */}
                            <div className="grid grid-cols-3 gap-2">
                                {tiers.map(tier => {
                                    const isActive = activeTier.id === tier.id;
                                    return (
                                        <div
                                            key={tier.id}
                                            className={`rounded-lg border-2 p-3 text-center transition-all ${
                                                isActive
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            <p className="text-xs font-bold text-gray-700">{tier.name.replace('Mayorista ', '').replace('Mayorista', 'Base')}</p>
                                            <p className={`text-lg font-extrabold ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
                                                {Math.abs(tier.percentage)}%
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Promo banner */}
                            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center">
                                <p className="text-sm text-green-800">
                                    <i className="fa fa-gift mr-1" />
                                    <strong>Promoción:</strong> tu primer mes tenés <strong>Mayorista Premium con {promoDiscount}% de descuento</strong> sin importar el volumen.
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full rounded-lg bg-green-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {submitting ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Creando empresa...
                                    </span>
                                ) : (
                                    'Continuar'
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                disabled={submitting}
                                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                            >
                                &larr; Volver
                            </button>
                        </form>
                    )}

                    {/* Step 3: Dirección de entrega */}
                    {step === 3 && (
                        <div>
                            {/* Summary */}
                            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                <p className="text-sm font-medium text-gray-700">{cuitData?.name}</p>
                                <p className="text-xs text-gray-500">CUIT: {cuitData?.cuit}</p>
                            </div>

                            {submitting ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-500 border-t-transparent" />
                                    <p className="mt-3 text-sm text-gray-500">Creando empresa...</p>
                                </div>
                            ) : hasExistingAddresses && !showNewForm ? (
                                <div className="space-y-4">
                                    <p className="text-sm font-medium text-gray-700">
                                        ¿Alguna de estas es la dirección de tu empresa?
                                    </p>
                                    <div className="space-y-2">
                                        {existingAddresses.map((addr) => {
                                            const isSelected = selectedAddressId === addr.id;
                                            return (
                                                <button
                                                    key={addr.id}
                                                    type="button"
                                                    onClick={() => setSelectedAddressId(isSelected ? null : addr.id)}
                                                    className={`w-full rounded-lg border-2 px-4 py-3 text-left transition-colors ${
                                                        isSelected
                                                            ? 'border-green-500 bg-green-50'
                                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                                    }`}
                                                >
                                                    <p className="text-sm font-medium text-gray-800">{addr.address}</p>
                                                    {addr.suite && (
                                                        <p className="text-xs text-gray-500">{addr.suite}</p>
                                                    )}
                                                    {addr.notes && (
                                                        <p className="text-xs text-gray-400">{addr.notes}</p>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {selectedAddressId && (
                                        <button
                                            type="button"
                                            onClick={() => submitCompany(selectedAddressId)}
                                            className="w-full rounded-lg bg-green-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-green-600"
                                        >
                                            Usar esta dirección
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedAddressId(null);
                                            setShowNewForm(true);
                                        }}
                                        className="w-full text-center text-sm font-medium text-green-600 hover:text-green-700"
                                    >
                                        + Agregar otra dirección
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <AddressForm
                                        routes={addressRoutes}
                                        onSuccess={handleAddressSuccess}
                                        hideMap
                                    />
                                    {hasExistingAddresses && (
                                        <button
                                            type="button"
                                            onClick={() => setShowNewForm(false)}
                                            className="mt-3 w-full text-center text-sm text-gray-500 hover:text-gray-700"
                                        >
                                            &larr; Volver a mis direcciones
                                        </button>
                                    )}
                                </>
                            )}

                            <button
                                type="button"
                                onClick={() => setStep(2)}
                                disabled={submitting}
                                className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                            >
                                &larr; Volver
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </MainLayout>
    );
}
